// CercleDigital InsiderGems, gestion électronique documentaire partagée de l'écosystème Cercle.
// Copyright 2026 Pierre-Thibault Fabre <pierre-thibault@cercle.digital>
// SPDX-License-Identifier: LicenseRef-Cercle-Interne
//
// Fichier : apps/web/src/lib/toasts.ts
// Rôle : porte les avis passagers — accusés, refus et échecs — affichés en bas
//        de l'écran.
// Référence : designsystem.md ; CIG-SPECIFICATION.md §5.3, §15.3, §19.

import { defineStore } from 'pinia';
import { ApiError } from './api.ts';

/**
 * Nature d'un avis. Elle commande la couleur et la durée, jamais le texte : la
 * couleur ne porte donc jamais seule l'information (designsystem.md).
 */
export type TonToast = 'neutre' | 'succes' | 'alerte' | 'danger';

export interface Toast {
  id: number;
  ton: TonToast;
  texte: string;
  /** Durée totale d'affichage, en millisecondes. */
  duree: number;
  /** Temps restant. Il commande la barre de minuterie et la disparition. */
  restant: number;
  /**
   * Répétitions absorbées. Un même avis émis trois fois n'empile pas trois
   * cartouches : il se recompte, et son délai repart.
   */
  repetitions: number;
  /** Suspendu : le pointeur ou le focus est dessus, on est en train de le lire. */
  suspendu: boolean;
}

/**
 * Durées d'affichage. Un accusé de réception se lit d'un regard ; un refus
 * demande de comprendre pourquoi, et un échec de le lire en entier.
 */
const DUREES: Record<TonToast, number> = {
  neutre: 4000,
  succes: 4000,
  alerte: 7000,
  danger: 9000,
};

/**
 * Avis affichés de front. Au-delà, la pile recouvre la vue qu'elle commente et
 * plus rien n'est lu : le plus ancien cède la place.
 */
const PILE_MAX = 4;

/**
 * Écart maximal imputé à un battement. Un onglet caché ne rend aucune image :
 * au retour, l'écart accumulé engloutirait d'un coup des avis que personne n'a
 * eu l'occasion de lire.
 */
const PAS_MAX = 250;

/**
 * Ton d'un refus, selon sa nature.
 *
 * Une fonction absente, suspendue ou hors de portée n'est pas une panne :
 * l'afficher en rouge ferait croire à un défaut de la plateforme là où elle
 * applique sa politique.
 */
const TON_PAR_CODE: Record<string, TonToast> = {
  ai_disabled: 'alerte',
  forbidden_feature: 'alerte',
  validation_failed: 'alerte',
  conflict: 'alerte',
  quota_exceeded: 'alerte',
  read_only: 'alerte',
  legal_hold: 'alerte',
  rate_limited: 'alerte',
  reauth_required: 'alerte',
};

/**
 * Chapeau posé devant le message du serveur.
 *
 * Celui-ci est exact, mais il décrit souvent une configuration plutôt qu'un
 * geste : « Aucun accès LLM assigné à la tâche « summary » » ne dit pas à
 * l'utilisateur que la fonction d'IA qu'il vient de demander est absente. Le
 * chapeau le lui dit, et le message d'origine est conservé tel quel — le
 * réécrire priverait l'administrateur de ce qui lui permet d'agir.
 */
const CHAPEAU: Record<string, string> = {
  ai_disabled: 'Fonction d\'IA indisponible',
  forbidden_feature: 'Fonction non autorisée',
  quota_exceeded: 'Quota atteint',
  read_only: 'Plateforme en lecture seule',
  legal_hold: 'Objet sous conservation légale',
  rate_limited: 'Trop de demandes',
  reauth_required: 'Réauthentification requise',
};

let sequence = 0;

export const useToasts = defineStore('toasts', {
  state: () => ({
    messages: [] as Toast[],
    /** Battement en cours, ou `null` tant qu'aucun avis n'est affiché. */
    battement: null as number | null,
    /** Instant du dernier battement, pour mesurer l'écart réellement écoulé. */
    instant: 0,
  }),

  actions: {
    /**
     * Dépose un avis. Un avis vide n'en est pas un et n'est pas déposé ; un
     * avis déjà à l'écran se recompte plutôt que de s'empiler.
     */
    poser(texte: string, ton: TonToast = 'neutre', duree = DUREES[ton]): number {
      const propre = texte.trim();
      if (!propre) return 0;

      const connu = this.messages.find((t) => t.texte === propre && t.ton === ton);
      if (connu) {
        connu.repetitions += 1;
        connu.duree = duree;
        connu.restant = duree;
        return connu.id;
      }

      sequence += 1;
      const toast: Toast = {
        id: sequence,
        ton,
        texte: propre,
        duree,
        restant: duree,
        repetitions: 1,
        suspendu: false,
      };
      this.messages = [...this.messages, toast].slice(-PILE_MAX);
      this.demarrer();
      return toast.id;
    },

    info(texte: string): number {
      return this.poser(texte, 'neutre');
    },

    succes(texte: string): number {
      return this.poser(texte, 'succes');
    },

    alerte(texte: string): number {
      return this.poser(texte, 'alerte');
    },

    /**
     * Signale un échec. Le ton suit la nature du refus, et le message reçoit
     * son chapeau lorsque le code d'erreur en prévoit un.
     */
    erreur(cause: unknown, repli = 'Erreur inattendue.'): number {
      if (cause instanceof ApiError) {
        const chapeau = CHAPEAU[cause.code];
        const texte = chapeau
          ? [chapeau, cause.message.trim()].filter(Boolean).join('. ')
          : cause.message;
        return this.poser(texte || repli, TON_PAR_CODE[cause.code] ?? 'danger');
      }
      const texte = cause instanceof Error ? cause.message : String(cause ?? '');
      return this.poser(texte || repli, 'danger');
    },

    /** Retire un avis. C'est ce que fait le clic. */
    retirer(id: number): void {
      this.messages = this.messages.filter((t) => t.id !== id);
    },

    /**
     * Suspend ou relance le décompte d'un avis. Un avis que l'on survole ou
     * que le clavier atteint est un avis que l'on est en train de lire : il ne
     * doit pas disparaître sous les yeux.
     */
    suspendre(id: number, valeur: boolean): void {
      const t = this.messages.find((x) => x.id === id);
      if (t) t.suspendu = valeur;
    },

    vider(): void {
      this.messages = [];
    },

    /**
     * Fait courir le temps.
     *
     * Le battement suit l'affichage : il démarre au premier avis et s'arrête
     * avec le dernier, plutôt que de tourner à vide toute la session. Il est
     * accroché au rendu, ce qui le fige naturellement lorsque l'onglet passe
     * en arrière-plan.
     */
    demarrer(): void {
      if (this.battement !== null) return;
      this.instant = performance.now();

      const battre = (): void => {
        const maintenant = performance.now();
        const ecoule = Math.min(maintenant - this.instant, PAS_MAX);
        this.instant = maintenant;
        this.ecouler(ecoule);
        this.battement = this.messages.length > 0 ? requestAnimationFrame(battre) : null;
      };

      this.battement = requestAnimationFrame(battre);
    },

    /** Décompte le temps écoulé et retire les avis arrivés à terme. */
    ecouler(ms: number): void {
      if (ms <= 0 || this.messages.length === 0) return;
      const survivants: Toast[] = [];
      for (const t of this.messages) {
        if (t.suspendu) {
          survivants.push(t);
          continue;
        }
        t.restant -= ms;
        if (t.restant > 0) survivants.push(t);
      }
      if (survivants.length !== this.messages.length) this.messages = survivants;
    },
  },
});
